import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  CalendarIcon, Check, X, Clock, AlertCircle, Users,
  Save, Loader2, ChevronLeft, ChevronRight
} from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'present', label: 'Present', icon: Check, color: 'text-green-600 bg-green-100' },
  { value: 'absent', label: 'Absent', icon: X, color: 'text-red-600 bg-red-100' },
  { value: 'late', label: 'Late', icon: Clock, color: 'text-amber-600 bg-amber-100' },
  { value: 'excused', label: 'Excused', icon: AlertCircle, color: 'text-blue-600 bg-blue-100' },
];

export default function InstructorAttendancePage() {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('section_id');
  
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(sectionId || '');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [roster, setRoster] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSections();
  }, []);

  useEffect(() => {
    if (selectedSection) {
      fetchRosterAndAttendance();
    }
  }, [selectedSection, selectedDate]);

  const fetchSections = async () => {
    try {
      const res = await api.get('/instructor/dashboard');
      setSections(res.data.sections || []);
      if (!selectedSection && res.data.sections?.length > 0) {
        setSelectedSection(res.data.sections[0].id);
      }
    } catch (error) {
      toast.error('Failed to load sections');
    }
  };

  const fetchRosterAndAttendance = async () => {
    setLoading(true);
    try {
      // Fetch roster
      const rosterRes = await api.get(`/instructor/roster?section_id=${selectedSection}`);
      setRoster(rosterRes.data.roster || []);
      
      // Fetch attendance for this date
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const attendanceRes = await api.get(`/attendance/section/${selectedSection}?meeting_date=${dateStr}`);
      
      // Convert attendance array to object keyed by learner_id
      const attendanceMap = {};
      (attendanceRes.data || []).forEach(record => {
        attendanceMap[record.learner] = record.status;
      });
      setAttendance(attendanceMap);
      setHasChanges(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const updateAttendance = (learnerId, status) => {
    setAttendance(prev => ({ ...prev, [learnerId]: status }));
    setHasChanges(true);
  };

  const markAllPresent = () => {
    const newAttendance = {};
    roster.forEach(student => {
      newAttendance[student.learner_id] = 'present';
    });
    setAttendance(newAttendance);
    setHasChanges(true);
  };

  const saveAttendance = async () => {
    setSaving(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      
      // Save each attendance record
      for (const [learnerId, status] of Object.entries(attendance)) {
        await api.post(`/attendance/section/${selectedSection}/mark/`, {
          learner_id: learnerId,
          meeting_date: dateStr,
          status: status,
        });
      }
      
      toast.success('Attendance saved successfully');
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + direction);
    setSelectedDate(newDate);
  };

  const sectionInfo = sections.find(s => s.id === selectedSection);

  // Stats
  const stats = {
    present: Object.values(attendance).filter(s => s === 'present').length,
    absent: Object.values(attendance).filter(s => s === 'absent').length,
    late: Object.values(attendance).filter(s => s === 'late').length,
    excused: Object.values(attendance).filter(s => s === 'excused').length,
    unmarked: roster.length - Object.keys(attendance).length,
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">Track student attendance for your classes</p>
        </div>
        {hasChanges && (
          <Button onClick={saveAttendance} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Attendance
          </Button>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={selectedSection} onValueChange={setSelectedSection}>
          <SelectTrigger className="w-[300px]">
            <SelectValue placeholder="Select a class" />
          </SelectTrigger>
          <SelectContent>
            {sections.map(section => (
              <SelectItem key={section.id} value={section.id}>
                {section.course_title} - {section.location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[200px]">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {format(selectedDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <Button variant="secondary" onClick={markAllPresent}>
          <Check className="h-4 w-4 mr-2" />
          Mark All Present
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Present</span>
              <Badge className="bg-green-100 text-green-700">{stats.present}</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Absent</span>
              <Badge className="bg-red-100 text-red-700">{stats.absent}</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Late</span>
              <Badge className="bg-amber-100 text-amber-700">{stats.late}</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Excused</span>
              <Badge className="bg-blue-100 text-blue-700">{stats.excused}</Badge>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Unmarked</span>
              <Badge variant="outline">{stats.unmarked}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Attendance Grid */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {sectionInfo?.course_title || 'Class Roster'}
            <Badge variant="outline">{roster.length} students</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : roster.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No students enrolled</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roster.map(student => {
                const currentStatus = attendance[student.learner_id];
                return (
                  <Card 
                    key={student.learner_id} 
                    className={`transition-colors ${
                      currentStatus === 'present' ? 'border-green-200 bg-green-50' :
                      currentStatus === 'absent' ? 'border-red-200 bg-red-50' :
                      currentStatus === 'late' ? 'border-amber-200 bg-amber-50' :
                      currentStatus === 'excused' ? 'border-blue-200 bg-blue-50' : ''
                    }`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium">{student.learner_name}</span>
                        {currentStatus && (
                          <Badge className={STATUS_OPTIONS.find(s => s.value === currentStatus)?.color}>
                            {STATUS_OPTIONS.find(s => s.value === currentStatus)?.label}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {STATUS_OPTIONS.map(option => {
                          const Icon = option.icon;
                          const isSelected = currentStatus === option.value;
                          return (
                            <Button
                              key={option.value}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              className={`flex-1 ${isSelected ? option.color : ''}`}
                              onClick={() => updateAttendance(student.learner_id, option.value)}
                            >
                              <Icon className="h-4 w-4" />
                            </Button>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
